import {zodResolver} from "@hookform/resolvers/zod";
import type {ICompany, UpdateLegalHeadquartersPayload} from "@mytour/shared";
import {Splitter} from "@mytour/ui/layout";
import {useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {Button, Form, FormFeedback, FormGroup, Input, Label, Spinner} from "reactstrap";
import {z} from "zod";
import {useUpdateLegalHeadquarters} from "../../../query/companySettingsQueries";
import {DromWarningBanner} from "./DromWarningBanner";
import {isDromAddress} from "./dromDetection";

type Props = {
  company: ICompany;
};

const headquartersSchema = z.object({
  address: z.string().min(1, "Champ obligatoire"),
  address2: z.string(),
  zip: z.string().regex(/^[0-9]{5}$/, "Le code postal en France est une suite de cinq chiffres"),
  city: z.string().min(1, "Champ obligatoire"),
});

type HeadquartersFormData = z.infer<typeof headquartersSchema>;

export function HeadquartersSubTab({company}: Props) {
  const {t: translate} = useTranslation();
  const {updateLegalHeadquarters, isUpdatingLegalHeadquarters} = useUpdateLegalHeadquarters();

  const disabled = isUpdatingLegalHeadquarters;

  const {
    register,
    handleSubmit,
    watch,
    formState: {errors},
  } = useForm<HeadquartersFormData>({
    resolver: zodResolver(headquartersSchema),
    defaultValues: {
      address: company.legal.administrativeAddress.streetAddress ?? "",
      address2: company.legal.administrativeAddress.streetAddress2 ?? "",
      zip: company.legal.administrativeAddress.zipCode ?? "",
      city: company.legal.administrativeAddress.city ?? "",
    },
  });

  const zip = watch("zip");

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      {isDromAddress(zip) && <DromWarningBanner />}
      <FormGroup>
        <Label for="address">{translate("myTourApp.platformUser.address")} *</Label>
        <Input
          id="address"
          innerRef={register("address").ref}
          onChange={register("address").onChange}
          onBlur={register("address").onBlur}
          name="address"
          invalid={!!errors.address}
        />
        {errors.address && <FormFeedback>{errors.address.message}</FormFeedback>}
      </FormGroup>

      <FormGroup>
        <Input
          id="address2"
          innerRef={register("address2").ref}
          onChange={register("address2").onChange}
          onBlur={register("address2").onBlur}
          name="address2"
        />
      </FormGroup>

      <Splitter>
        <FormGroup>
          <Label for="zip">{translate("myTourApp.platformUser.zip")} *</Label>
          <Input
            id="zip"
            innerRef={register("zip").ref}
            onChange={register("zip").onChange}
            onBlur={register("zip").onBlur}
            name="zip"
            invalid={!!errors.zip}
          />
          {errors.zip && <FormFeedback>{errors.zip.message}</FormFeedback>}
        </FormGroup>
        <FormGroup>
          <Label for="city">{translate("myTourApp.platformUser.city")} *</Label>
          <Input
            id="city"
            innerRef={register("city").ref}
            onChange={register("city").onChange}
            onBlur={register("city").onBlur}
            name="city"
            invalid={!!errors.city}
          />
          {errors.city && <FormFeedback>{errors.city.message}</FormFeedback>}
        </FormGroup>
      </Splitter>

      <Button color="primary" type="submit" disabled={disabled}>
        {isUpdatingLegalHeadquarters ? <Spinner size="sm" /> : null}
        {isUpdatingLegalHeadquarters ? " " : ""}Sauvegarder
      </Button>
    </Form>
  );

  async function onSubmit(data: HeadquartersFormData) {
    const payload: UpdateLegalHeadquartersPayload = {
      legalAddress: data.address,
      legalAddress2: data.address2,
      legalCity: data.city,
      legalZip: data.zip,
    };

    try {
      await updateLegalHeadquarters(payload);
      toast.success("Siège social enregistré");
    } catch {
      toast.error("Erreur lors de la sauvegarde du siège social");
    }
  }
}
